import * as React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { LinkTo } from "@/components/icons/link-to";
import { Description } from "@/components/import-list/import-list-component-styles";
import { Card } from "@/components/shared/card";
import useAnalytics from "@/hooks/use-analytics";
import endpoints from "@/utils/endpoints";

import { ColorLabels } from "../editor/utils/use-color";
import { Link } from "../link";

const get = endpoints();

type SyncedListNoticeProps = {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  children: React.ReactNode;
  path: string;
  title: string;
  eventSlug: string;
  minimal?: boolean;
};

const StyleWrapper = styled.div`
  color: ${({ theme }) => theme.colors[ColorLabels.SECONDARY_FONT]};
`;

export default function SyncedListNotice({
  icon: Icon,
  children,
  path,
  title,
  eventSlug,
  minimal = false,
}: SyncedListNoticeProps) {
  const linkRef = React.useRef<HTMLAnchorElement | null>(null);

  const { logAnalytics } = useAnalytics();

  React.useEffect(() => {
    function handleOpenDashboardClick() {
      logAnalytics("CANVAS_EDITOR_PREFERENCE_PANEL_BUTTON", {
        button_text: "open_in_dashboard",
      });
    }

    const button = linkRef.current;

    if (button) {
      button.addEventListener("click", handleOpenDashboardClick);
    }

    return () => {
      if (button) {
        button.removeEventListener("click", handleOpenDashboardClick);
      }
    };
  }, [logAnalytics]);

  if (minimal) {
    return (
      <StyleWrapper>
        <div className="flex flex-col items-center">
          <InnerMessage eventSlug={eventSlug} path={path} linkRef={linkRef}>
            {children}
          </InnerMessage>
        </div>
      </StyleWrapper>
    );
  }

  return (
    <Card>
      <div className="flex flex-col items-center">
        <Icon className="size-6 text-blue-500" />
        <h2 className="mt-4 text-center text-2xl font-semibold text-gray-800">
          {title}
        </h2>
        <InnerMessage eventSlug={eventSlug} path={path} linkRef={linkRef}>
          {children}
        </InnerMessage>
      </div>
    </Card>
  );
}

type InnerMessageProps = {
  linkRef: React.MutableRefObject<HTMLAnchorElement>;
  children: React.ReactNode;
  eventSlug: string;
  path: string;
};

const InnerMessage = ({
  linkRef,
  children,
  eventSlug,
  path,
}: InnerMessageProps) => {
  const { t } = useTranslation();

  return (
    <>
      <Description>{children}</Description>
      <Link
        href={get("HOPIN_DASHBOARD_PATH")(eventSlug, path)}
        ref={linkRef}
        target="_blank"
        rel="noreferrer noopener"
        variant="outlined"
        size="small"
        className="border-solid px-7"
        data-testid="open-dashboard-button"
      >
        {t("preference-panel.edit-in-dashboard")}
        <LinkTo className="ml-4 size-6 text-blue-400" />
      </Link>
    </>
  );
};
